import { useCallback, useEffect, useMemo, useState } from 'react'
import { Coins, Hand, Download, Layers2, ShieldCheck, ShieldOff, Split } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CurrencyIcon } from '@/components/profile/CurrencyIcon'
import { formatBalance } from '@/lib/format-number'
import { cn } from '@/lib/utils'
import { useGetCurrenciesQuery } from '@/store/api/paymentApi'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { selectBlackjackGame, setBlackjackBetAmount } from '@/store/slices/blackjackSlice'
import type { GameController } from '@/components/original/games/blackjack/core/GameController'

interface BlackjackBetPanelProps {
	controller: GameController | null
	className?: string
}

export function BlackjackBetPanel({ controller, className }: BlackjackBetPanelProps) {
	const dispatch = useAppDispatch()
	const blackjack = useAppSelector(selectBlackjackGame)
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const { data: currenciesResponse } = useGetCurrenciesQuery()
	const [amountDraft, setAmountDraft] = useState(blackjack.betAmount)

	const currencies = currenciesResponse?.data ?? []
	const currency = useMemo(() => currencies.find((item) => item.currency_code.toUpperCase() === blackjack.currencyCode.toUpperCase()), [currencies, blackjack.currencyCode])

	const usdAmount = useMemo(() => {
		const amount = Number(amountDraft)
		const rate = Number(currency?.exchange_rate ?? 0)

		if (!Number.isFinite(amount) || !Number.isFinite(rate)) {
			return 0
		}

		return amount * rate
	}, [amountDraft, currency?.exchange_rate])

	const isLive = blackjack.phase === 'live'
	const isInsurance = blackjack.phase === 'insurance'
	const controlsLocked = blackjack.isSubmitting || blackjack.isClearing
	const amountLocked = isLive || isInsurance || controlsLocked

	useEffect(() => {
		setAmountDraft(blackjack.betAmount)
	}, [blackjack.betAmount])

	const commitAmount = useCallback(() => {
		const parsed = Number(amountDraft)
		if (!Number.isFinite(parsed) || parsed <= 0) {
			setAmountDraft(blackjack.betAmount)
			return
		}

		dispatch(setBlackjackBetAmount(parsed.toString()))
	}, [amountDraft, blackjack.betAmount, dispatch])

	const handleQuickAmount = useCallback(
		(multiplier: number) => {
			const parsed = Number(amountDraft) || Number(blackjack.betAmount) || 0
			const next = Math.max(0, parsed * multiplier)
			const nextAmount = next > 0 ? String(next) : '0'

			setAmountDraft(nextAmount)
			dispatch(setBlackjackBetAmount(nextAmount))
		},
		[amountDraft, blackjack.betAmount, dispatch],
	)

	const primaryAction = useMemo(() => {
		if (!isAuthenticated) {
			return { label: 'Bet', variant: 'secondary' as const, disabled: true, onClick: () => undefined }
		}

		if (isInsurance || isLive) {
			return {
				label: 'Bet',
				variant: 'secondary' as const,
				disabled: true,
				onClick: () => undefined,
			}
		}

		return {
			label: 'Bet',
			variant: 'default' as const,
			disabled: controlsLocked,
			onClick: () => void controller?.startGame(),
		}
	}, [controlsLocked, controller, isAuthenticated, isInsurance, isLive])

	return (
		<div className={cn('flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden rounded-2xl border border-border bg-card p-4', className)}>
			<div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto'>
			<div className={cn('space-y-2', amountLocked && 'pointer-events-none opacity-50')}>
				<div className='flex items-center justify-between gap-3'>
					<label className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>Bet Amount</label>
					<span className='text-xs font-medium text-muted-foreground'>${formatBalance(usdAmount, 2)}</span>
				</div>
				<div className='flex items-stretch overflow-hidden rounded-xl border border-border bg-secondary'>
					<div className='relative min-w-0 flex-1'>
						<input
							type='text'
							inputMode='decimal'
							value={amountDraft}
							disabled={amountLocked}
							onChange={(event) => setAmountDraft(event.target.value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1'))}
							onBlur={commitAmount}
							className='flex h-11 w-full bg-transparent pl-3 text-sm font-semibold tracking-[-0.21px] text-foreground focus:outline-none sm:text-base sm:tracking-[-0.24px]'
						/>
						<CurrencyIcon
							code={blackjack.currencyCode}
							symbolUrl={currency?.symbol_url}
							size='sm'
							className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2'
						/>
					</div>
					<div className='flex w-[88px] shrink-0 border-l border-border'>
						<button
							type='button'
							disabled={amountLocked}
							onClick={() => handleQuickAmount(0.5)}
							className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							½
						</button>
						<span className='w-px bg-border' aria-hidden />
						<button
							type='button'
							disabled={amountLocked}
							onClick={() => handleQuickAmount(2)}
							className='flex-1 text-sm font-medium text-foreground transition-colors hover:bg-accent disabled:opacity-50'
						>
							2×
						</button>
					</div>
				</div>
			</div>

			{isInsurance ? (
				<div className='space-y-3 rounded-xl border border-border bg-secondary/60 p-4'>
					<p className='text-center text-sm font-semibold text-foreground'>Insurance?</p>
					<div className='grid grid-cols-2 gap-3'>
						<Button type='button' disabled={controlsLocked || !blackjack.canInsurance} onClick={() => void controller?.acceptInsurance()} className='h-11'>
							<ShieldCheck className='text-success' />
							Accept
						</Button>
						<Button type='button' variant='outline' disabled={controlsLocked} onClick={() => void controller?.declineInsurance()} className='h-11'>
							<ShieldOff className='text-destructive' />
							No
						</Button>
					</div>
				</div>
			) : (
				<div className='grid grid-cols-2 gap-3'>
					<Button type='button' variant='secondary' disabled={!isLive || controlsLocked || !blackjack.canHit} onClick={() => void controller?.hit()} className='h-11'>
						<Download className='text-info' />
						Hit
					</Button>
					<Button type='button' variant='secondary' disabled={!isLive || controlsLocked || !blackjack.canStand} onClick={() => void controller?.stand()} className='h-11'>
						<Hand className='text-warning' />
						Stand
					</Button>
					<Button type='button' variant='secondary' disabled={!isLive || controlsLocked || !blackjack.canSplit} onClick={() => void controller?.split()} className='h-11'>
						<Split className='text-primary' />
						Split
					</Button>
					<Button
						type='button'
						variant='secondary'
						disabled={!isLive || controlsLocked || !blackjack.canDouble}
						onClick={() => void controller?.doubleDown()}
						className='h-11'
					>
						<Layers2 className='text-success' />
						Double
					</Button>
				</div>
			)}

			{(isLive || isInsurance) && blackjack.currentMultiplier > 0 ? (
				<div className='rounded-xl border border-border bg-secondary/60 px-4 py-3'>
					<p className='text-xs uppercase tracking-wide text-muted-foreground'>Current</p>
					<p className='mt-1 text-lg font-semibold text-foreground'>
						{blackjack.currentMultiplier.toFixed(2)}× · {formatBalance(blackjack.currentPayout, 4)}
					</p>
				</div>
			) : null}
			</div>

			<div className='shrink-0'>
				<Button
					type='button'
					variant={primaryAction.variant}
					disabled={primaryAction.disabled || !controller}
					onClick={primaryAction.onClick}
					className='h-12 w-full text-base font-semibold'
				>
					{primaryAction.label}
				</Button>
			</div>
		</div>
	)
}
